'use client';

import { c as _c } from "react/compiler-runtime";
import type { AuthorProfile } from '@svenskaspelab/poolbetting/schema/content';
import type { Definition } from '@svenskaspelab/poolbetting/schema/frameworks';
import * as TipsenSheet from '@svenskaspelab/poolbetting/ui/TipsenSheet';
import Information from '@svenskaspelab/ui/react/icons/Information';
import { RadioGroup, RadioGroupItem } from '@svenskaspelab/ui/react/radio-group';
import { showSnackbar, Snackbar } from '@svenskaspelab/ui/react/snackbar';
import { ToggleGroup, ToggleGroupItem } from '@svenskaspelab/ui/react/toggle-group';
import { type ReactNode, type SubmitEvent, useEffect, useMemo } from 'react';
type QuickPickFormProps = {
  quickPickSelections: Definition['quickPickMsys'] | Definition['quickPickSrow'];
  handleSubmit: (event: SubmitEvent<HTMLFormElement>) => Promise<void>;
  isPending: boolean;
  isError: boolean;
  quickPickSize: number;
  setQuickPickSize: (size: number) => void;
  quickPickBase: 'ownChoice' | 'emptyCoupon' | `analysis-${string}`;
  setQuickPickBase: (value: 'ownChoice' | 'emptyCoupon' | `analysis-${string}`) => void;
  authorProfiles: AuthorProfile[];
  isValueDisabled: (value: number) => boolean;
  isSingleRow: boolean;
  shouldShowRadioGroup: boolean;
};

/**
 * Quick pick form component.
 *
 * @param root0 component props
 * @param root0.quickPickSelections Quick pick selections values to render
 * @param root0.handleSubmit Form submit handler
 * @param root0.isPending Indicates if the form submission is pending
 * @param root0.isError Indicates if there was an error during form submission
 * @param root0.quickPickSize Currently selected quick pick size
 * @param root0.setQuickPickSize Function to update the selected quick pick size
 * @param root0.quickPickBase Currently selected value for the quick pick base
 * @param root0.setQuickPickBase Function to update the selected value for the quick pick base
 * @param root0.authorProfiles Game analysis data for authors
 * @param root0.isValueDisabled Function to determine if a quick pick value is disabled
 * @param root0.isSingleRow Indicates if the quick pick is for a single row coupon
 * @param root0.shouldShowRadioGroup Indicates if the radio group for quick pick base selection should be shown
 * @returns Quick pick form component
 * @throws {Error} Will throw an error if draw number is not available
 */
export default function QuickPickForm(t0) {
  const $ = _c(44);
  const {
    quickPickSelections,
    handleSubmit,
    isPending,
    isError,
    quickPickSize,
    setQuickPickSize,
    quickPickBase,
    setQuickPickBase,
    authorProfiles,
    isValueDisabled,
    isSingleRow,
    shouldShowRadioGroup
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2"><span className="text-base"><Information /></span><span>Hoppsan! Något gick fel, försök igen.</span></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const snackbarText = t1;
  let t2;
  let t3;
  if ($[1] !== isError) {
    t2 = () => {
      if (isError) {
        showSnackbar({
          text: snackbarText,
          variant: "important",
          showCloseButton: true
        });
      }
    };
    t3 = [isError, snackbarText];
    $[1] = isError;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== handleSubmit) {
    t4 = event => {
      handleSubmit(event);
    };
    $[4] = handleSubmit;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = isSingleRow ? "V\xE4lj antal rader" : "V\xE4lj belopp";
  let t6;
  if ($[6] !== t5) {
    t6 = <legend className="pb-4 font-medium">{t5}</legend>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = isValueDisabled(quickPickSize) ? "" : `${quickPickSize}`;
  let t8;
  if ($[8] !== setQuickPickSize) {
    t8 = value => {
      if (value === "") {
        return;
      }
      setQuickPickSize(Number(value));
    };
    $[8] = setQuickPickSize;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isSingleRow || $[11] !== isValueDisabled || $[12] !== quickPickSelections || $[13] !== quickPickSize) {
    t9 = quickPickSelections?.map(msys => {
      let quickPickValue = `${msys} kr`;
      if (isSingleRow) {
        quickPickValue = msys === 1 ? `${msys} rad` : `${msys} rader`;
      }
      return <ToggleGroupItem role="radio" aria-checked={quickPickSize === msys} data-state={quickPickSize === msys ? "on" : "off"} className="border-stroke-medium h-10 w-full rounded-none border border-r-0 border-b-0 whitespace-nowrap first:rounded-l-md data-[state=on]:border nth-[-n+3]:border-t-0 data-[state=on]:nth-[-n+3]:border-t nth-[3n+1]:border-l-0 data-[state=on]:nth-[3n+1]:border-l" key={msys} value={`${msys}`} disabled={isValueDisabled(msys)}>{quickPickValue}</ToggleGroupItem>;
    });
    $[10] = isSingleRow;
    $[11] = isValueDisabled;
    $[12] = quickPickSelections;
    $[13] = quickPickSize;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <ToggleGroup className="border-stroke-medium grid w-full grid-cols-3 gap-0 rounded-none border *:first:rounded-none *:last:rounded-none" type="single" context="base" emphasis="low" value={t7} onValueChange={t8}>{t9}</ToggleGroup>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6) {
    t11 = <fieldset>{t6}{t10}</fieldset>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== authorProfiles || $[23] !== quickPickBase || $[24] !== setQuickPickBase || $[25] !== shouldShowRadioGroup) {
    t12 = shouldShowRadioGroup && <fieldset><legend className="pt-6 pb-4 font-medium">Utgå ifrån</legend><RadioGroup value={quickPickBase} onValueChange={value_0 => {
        setQuickPickBase(value_0 as 'ownChoice' | 'emptyCoupon' | `analysis-${string}`);
      }} orientation="vertical"><div className="grid auto-cols-max grid-flow-col items-center gap-2"><RadioGroupItem aria-label="Egna val" id="ownChoice" value="ownChoice" /><label htmlFor="ownChoice">Egna val</label></div><div className="grid auto-cols-max grid-flow-col items-center gap-2"><RadioGroupItem aria-label="Tom kupong" id="emptyCoupon" value="emptyCoupon" /><label htmlFor="emptyCoupon">Tom kupong</label></div>{authorProfiles.length > 0 && authorProfiles.map(_temp)}</RadioGroup></fieldset>;
    $[22] = authorProfiles;
    $[23] = quickPickBase;
    $[24] = setQuickPickBase;
    $[25] = shouldShowRadioGroup;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Snackbar />;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t12) {
    t14 = <TipsenSheet.Content>{t11}{t12}{t13}</TipsenSheet.Content>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== isError || $[32] !== isPending || $[33] !== isValueDisabled || $[34] !== quickPickSize) {
    t15 = isPending || isError || isValueDisabled(quickPickSize);
    $[31] = isError;
    $[32] = isPending;
    $[33] = isValueDisabled;
    $[34] = quickPickSize;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = <TipsenSheet.Footer><TipsenSheet.Close type="submit" disabled={t15}>Generera PIX!</TipsenSheet.Close></TipsenSheet.Footer>;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t14 || $[39] !== t16) {
    t17 = <div className="grid h-full grid-rows-[auto_1fr_auto]">{t14}{t16}</div>;
    $[38] = t14;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t17 || $[42] !== t4) {
    t18 = <form onSubmit={t4} className="h-full">{t17}</form>;
    $[41] = t17;
    $[42] = t4;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
}
function _temp(authorProfile) {
  return <div className="grid auto-cols-max grid-flow-col items-center gap-2" key={authorProfile.id}><RadioGroupItem aria-label={`${authorProfile.longName}s analys`} id={`analysis-${authorProfile.id}`} value={`analysis-${authorProfile.id}`} /><label htmlFor={`analysis-${authorProfile.id}`}>{`${authorProfile.longName}s analys`}</label></div>;
}